/* UTILS */
import { computed, ref, nextTick } from "vue"
import { defineStore, storeToRefs } from "pinia"
import { useCookies } from "@vueuse/integrations/useCookies"
import { COOKIES } from "@/config/constants"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { buildCompanyLabel } from "@/helpers/format"

/* STORES */
import { useConfigStore } from "@/stores/config"
import { useUserStore } from "@/stores/user"
import { useI18nStore } from "@/stores/i18n"

export const SCREENING_AVAILABLE_LANGUAGES = [
  "en-GB",
  "en-US",
  "fr-FR",
  "de-DE",
  "nl-NL",
  "nl-BE",
  "es-ES",
  "it-IT",
]

export const useAnswersStore = defineStore("screeningAnswersStore", () => {
  const userStore = useUserStore()
  const i18nStore = useI18nStore()

  const { nextAssessmentExtraData } = storeToRefs(userStore)
  const { fetchNextAssessment } = userStore
  const cookies = useCookies()
  const { currentLocale, currentLang } = storeToRefs(i18nStore)
  const { getLocaleIdFromIsoCode } = i18nStore

  const screeningLang = ref(
    getLocaleIdFromIsoCode(currentLocale.value.iso_code)
  )

  const SCREENING_COOKIE_OPTIONS = {
    domain: useConfigStore().urls.cookie_domain,
    httpOnly: false,
    secure: true,
    sameSite: "none" as const,
  }

  // STORAGE DATA
  // We store the microphone and speaker id in the local storage to keep the user choice
  // In case of refresh or tab close, the user will have the same choice
  const selectedMicrophoneId = ref<string>(
    localStorage.getItem("selectedMicrophoneId") || ""
  )
  const selectedSpeakerId = ref<string>(
    localStorage.getItem("selectedSpeakerId") || ""
  )

  const screeningToken = ref<string>(
    localStorage.getItem("screening-token") || ""
  )

  // COOKIES DATA

  const companyName = computed(() =>
    cookies.get(COOKIES.SCREENING_COMPANY_NAME)
  )

  const companyNameWithSharepool = computed(() => {
    const sharepoolLabel =
      cookies.get(COOKIES.SCREENING_SHAREPOOL_LABEL) ??
      nextAssessmentExtraData.value?.sharepool_label

    return buildCompanyLabel(companyName.value, sharepoolLabel)
  })

  const pmUuid = computed(() => cookies.get(COOKIES.PM_UUID))

  const roleName = computed(() => cookies.get(COOKIES.SCREENING_PM_NAME))

  const trackMixpanelEvent = (
    eventName: string,
    entryPoint: string,
    extra: Record<string, unknown> = {}
  ) => {
    MixpanelInstance.track(eventName, {
      ...extra,
      entry_point: entryPoint,
      language: currentLang.value,
      assessment: "scan",
    })
  }

  const fetchScreeningData = async () => {
    const pm_uuid = useCookies().get(COOKIES.PM_UUID)
    const company_uuid = useCookies().get(COOKIES.COMPANY_UUID)

    await fetchNextAssessment(pm_uuid ?? null, company_uuid ?? null)
    await nextTick()
  }

  const setMicrophoneId = (id: string) => {
    selectedMicrophoneId.value = id
    localStorage.setItem("selectedMicrophoneId", id)
  }

  const setSpeakerId = (id: string) => {
    selectedSpeakerId.value = id
    localStorage.setItem("selectedSpeakerId", id)
  }

  const setScreeningToken = (token: string) => {
    screeningToken.value = token
    localStorage.setItem("screening-token", token)
  }

  const clearScreeningStorage = () => {
    localStorage.removeItem("selectedMicrophoneId")
    localStorage.removeItem("selectedSpeakerId")
    localStorage.removeItem("screening-token")
  }
  const clearScreeningCookies = () => {
    cookies.remove(COOKIES.SCREENING_AGENT_HR, SCREENING_COOKIE_OPTIONS)
    cookies.remove(COOKIES.SCREENING_PM_NAME, SCREENING_COOKIE_OPTIONS)
    cookies.remove(COOKIES.SCREENING_COMPANY_NAME, SCREENING_COOKIE_OPTIONS)
    cookies.remove(COOKIES.SCREENING_SHAREPOOL_LABEL, SCREENING_COOKIE_OPTIONS)
  }

  return {
    companyName,
    companyNameWithSharepool,
    selectedMicrophoneId,
    selectedSpeakerId,
    screeningToken,
    pmUuid,
    roleName,
    screeningLang,
    setMicrophoneId,
    setSpeakerId,
    setScreeningToken,
    clearScreeningStorage,
    clearScreeningCookies,
    fetchScreeningData,
    trackMixpanelEvent,
  }
})
